import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_URLS } from 'constants/urls';

/** Return type for useHomeNavigation hook */
interface UseHomeNavigationResult {
  /** Navigate to edit mode, preserving active tab */
  handleNavigateToEdit: () => void;
  /** Navigate to view mode, preserving active tab */
  handleNavigateToView: () => void;
  /** Navigate to widget templates management page */
  handleNavigateToWidgetTemplates: () => void;
}

/**
 * Hook for managing home page navigation
 * Provides handlers for navigating between view/edit modes and to widget templates
 */
export default function useHomeNavigation(
  activeHomePageId: string | undefined,
): UseHomeNavigationResult {
  const navigate = useNavigate();

  // Navigate to edit mode with current tab preserved
  const handleNavigateToEdit = useCallback(() => {
    const tabParam = activeHomePageId ? `?tab=${activeHomePageId}` : '';
    navigate(`${APP_URLS.dashboard.homeEdit.path}${tabParam}`);
  }, [navigate, activeHomePageId]);

  // Navigate to view mode with current tab preserved
  const handleNavigateToView = useCallback(() => {
    const tabParam = activeHomePageId ? `?tab=${activeHomePageId}` : '';
    navigate(`${APP_URLS.dashboard.home.path}${tabParam}`);
  }, [navigate, activeHomePageId]);

  // Navigate to widget templates management
  const handleNavigateToWidgetTemplates = useCallback(() => {
    navigate(APP_URLS.dashboard.homeWidgets.path);
  }, [navigate]);

  return {
    handleNavigateToEdit,
    handleNavigateToView,
    handleNavigateToWidgetTemplates,
  };
}
